import { useEffect, useMemo, useState } from 'react';
import { StanDropdown, StanLoader, StanModal, StanProgressBar } from '@/components';
import { StanCheckbox } from '@/components/core-components/StanCheckbox/StanCheckbox';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { useBulkCustomerActions } from '../../hooks/useBulkCustomerActions';
import { type AffiliateProductOption, useAffiliateProducts } from '../../hooks/useAffiliateProducts';
import type { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';

interface BulkGrantAffiliateModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalCustomers: number;
    filterArray: string | undefined;
}

export function BulkGrantAffiliateModal({
    isOpen,
    onClose,
    totalCustomers,
    filterArray,
}: BulkGrantAffiliateModalProps) {
    const [selectedProduct, setSelectedProduct] = useState<AffiliateProductOption | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { dropdownOptions, findProductByPageId } = useAffiliateProducts();

    const { batchState, isLoading, grantAffiliateAccess, resetState } = useBulkCustomerActions({
        filterArray,
        totalCustomers,
        onSuccess: () => {
            setSelectedProduct(null);
            setConfirmed(false);
            setError(null);
            onClose();
        },
    });

    useEffect(() => {
        if (isOpen) {
            setSelectedProduct(null);
            setConfirmed(false);
            setError(null);
            resetState();
        }
    }, [isOpen, resetState]);

    const handleClose = () => {
        if (!isLoading) {
            setSelectedProduct(null);
            setConfirmed(false);
            setError(null);
            resetState();
            onClose();
        }
    };

    const handleProductSelect = (value: DropdownValue) => {
        const product = findProductByPageId(value.value);
        if (product) {
            setSelectedProduct(product);
            setError(null);
        }
    };

    const handleSubmit = () => {
        if (!selectedProduct) {
            setError('Please select a product!');
            return;
        }

        if (!confirmed) {
            return;
        }

        setError(null);
        grantAffiliateAccess(selectedProduct.page_id);
    };

    const modalTitle = useMemo(() => {
        if (isLoading) return 'Processing...';
        const customerText = totalCustomers === 1 ? 'customer' : 'customers';
        return `Grant affiliate access to ${totalCustomers} ${customerText}`;
    }, [isLoading, totalCustomers]);

    const customerText = totalCustomers === 1 ? 'customer' : 'customers';

    const currentValue = selectedProduct
        ? { label: selectedProduct.name, value: String(selectedProduct.page_id) }
        : null;

    return (
        <StanModal
            id='bulk-grant-affiliate-modal'
            isOpen={isOpen}
            title={modalTitle}
            onClose={handleClose}
            primaryActionLabel={isLoading ? undefined : 'Grant Affiliate Access'}
            secondaryActionLabel={isLoading ? undefined : 'Cancel'}
            primaryActionDisabled={!selectedProduct || !confirmed}
            primaryActionLoading={isLoading}
            onPrimaryAction={handleSubmit}
            onSecondaryAction={handleClose}
            size='md'
        >
            <div className='flex flex-col gap-4'>
                {isLoading ? (
                    <div className='flex flex-col items-center gap-6 py-8'>
                        <StanLoader />
                        <StanProgressBar
                            min={0}
                            max={batchState.totalCount}
                            value={batchState.processedCount}
                            className='w-full'
                        />
                        <p className='text-gray text-label-xs-regular text-center'>
                            {batchState.processedCount} / {batchState.totalCount} customers processed
                        </p>
                    </div>
                ) : (
                    <div className='flex flex-col gap-4'>
                        {/* Product selection */}
                        <DropdownProvider
                            dropdownId='affiliate-product-select'
                            dropdownName='affiliate-product-select'
                            initialValue={currentValue}
                        >
                            <StanDropdown
                                id='affiliate-product-select'
                                name='affiliate-product-select'
                                options={dropdownOptions}
                                onSelect={handleProductSelect}
                                placeholder='Select a product'
                                currentValue={currentValue}
                            />
                        </DropdownProvider>
                        {error && <p className='text-negative text-label-xs-regular'>{error}</p>}

                        {/* Confirmation checkbox */}
                        <label className='mt-2 flex cursor-pointer items-center gap-2 rounded-xl p-4'>
                            <StanCheckbox
                                checked={confirmed}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmed(e.target.checked)}
                            />
                            <span className='text-label-sm-regular text-dark'>
                                Are you sure you want to grant affiliate access to{' '}
                                <span className='text-brand-main'>
                                    {totalCustomers} {customerText}
                                </span>
                                ?
                            </span>
                        </label>
                    </div>
                )}
            </div>
        </StanModal>
    );
}

BulkGrantAffiliateModal.displayName = 'BulkGrantAffiliateModal';
